/**
 * Block file-level operations — WBLOCK / PURGE / RENAME.
 *
 * These three mutations sit above the primitive BLOCK / INSERT / EXPLODE
 * helpers in `blockMutations.ts`. They work on the full DrawingDocument:
 *
 *   - wblockExportMutation(input) — build and serialise a new DrawingDocument
 *       from one of three sources:
 *         (a) a single named block (its entities become *Model_Space)
 *         (b) the entire current drawing (deep clone)
 *         (c) a selection of entities (translated to origin)
 *       The result is a DXF text blob ready for `URL.createObjectURL` on the
 *       UI side. No reducer case — WBLOCK is a side-effect (browser download).
 *
 *   - purgeBlocksMutation(input) — analyse which blocks are unreferenced
 *       and safe to purge. Returns a list of candidates with explanations
 *       (`canPurge`, `reason`, `dependentBlocks`). Reserved (anonymous)
 *       blocks are excluded unless `skipReserved: false`. The UI calls this
 *       first to populate the PurgeBlocksDialog checklist, then dispatches
 *       `file/purge-blocks` with the user-confirmed name set which runs
 *       `applyPurgeBlocks` inside the reducer.
 *
 *   - renameBlockMutation(input) — rename a block with full cascade:
 *       updates blocks[newName] and blockRecords[newName], removes the old
 *       entries, and walks every INSERT entity (including nested INSERTs
 *       inside other blocks) to rewrite the blockName reference. Layout
 *       blocks (*Paper_SpaceN) also update their corresponding layout entry
 *       when present.
 *
 * Every function is pure: the input DrawingDocument is never mutated; a
 * freshly-parsed (and validated) document is returned. That way the
 * geometryReducer can call these directly and pass the result to
 * `drawingDocumentSchema.parse(...)` for a final safety check.
 */

import type {
  ArrayRectEntity,
  BlockChildEntity,
  DrawingDocument,
  DrawingEntity,
} from "@dxf-ai-editor/drawing-model";
import {
  arrayRectEmitsAsMInsert,
  assembleDrawingDocument,
  blockRecordHandlesToNameFromDocument,
  buildDependencyIndex,
  carriedTableStyleRecord,
  drawingDocumentSchema,
  exportDrawingDocumentToDxfString,
  extractRefs,
  isAnonymousBlockName,
  isRenameHiddenBlockName,
  resolveSourceBlockName,
  type CarriedObjectRecord,
} from "@dxf-ai-editor/drawing-model";

import { validateBlockName } from "./blockMutations";
import { transformEntityCoords, translatePoint } from "./geometryTransforms";

// ═══════════════════════════════════════════════════════════════
// WBLOCK — export a block / drawing / selection to DXF text
// ═══════════════════════════════════════════════════════════════

/**
 * The three flavours of WBLOCK matching AutoCAD's "Write Block" dialog:
 *   - kind="block"   → export a named block as the *Model_Space of a new doc
 *   - kind="drawing" → export the entire active drawing verbatim
 *   - kind="objects" → export a selection as the new doc's *Model_Space,
 *                      translated so basePoint becomes the origin
 */
export type WblockSource =
  | { kind: "block"; blockName: string }
  | { kind: "drawing" }
  | {
      kind: "objects";
      entityIds: string[];
      basePoint: { x: number; y: number };
    };

export interface WblockExportInput {
  source: WblockSource;
  /** User-supplied filename. `.dxf` is appended if missing. */
  filename: string;
  /** INSUNITS value for the exported document (0..21). */
  insertionUnits: number;
  /** Source drawing document — never mutated. */
  drawingDocument: DrawingDocument;
}

export interface WblockExportResult {
  /** Serialised DXF content ready for Blob / download. */
  dxfText: string;
  /** Filename with `.dxf` extension guaranteed. */
  suggestedFilename: string;
  /** Human-readable summary for the command feed. */
  summary: string;
}

function ensureDxfExtension(filename: string): string {
  const trimmed = filename.trim();
  if (trimmed.length === 0) return "untitled.dxf";
  const lower = trimmed.toLowerCase();
  if (lower.endsWith(".dxf")) return trimmed;
  return `${trimmed}.dxf`;
}

/**
 * Rebase one entity so the user-picked WBLOCK base point becomes the origin.
 *
 * This is a MOVE by `(-dx, -dy)` and nothing else, so it delegates to the ONE
 * entity transformer — the identical expression the MOVE reducer builds
 * (`geometryReducerEdit.ts`: `transformEntityCoords(entity, (p) =>
 * translatePoint(p, dx, dy))`). It used to be a hand-maintained LIST of point
 * FIELD NAMES (`start` / `end` / `center` / … plus a generic `vertices.map`),
 * i.e. exactly the silent-drop shape this repo keeps paying for: every entity
 * kind whose coordinates live anywhere else was left at its ORIGINAL position
 * while its siblings moved, changing the relative placement of the exported
 * drawing (@codex round 16, FINDING 2).
 *
 * The two newly-admitted kinds the round-16 finding names are the proof:
 *   - an `OLE Frame` keeps its corners in `upperLeft` / `lowerRight`, names the
 *     old list did not carry at all;
 *   - a heavy `Polyline`'s `vertices` are `{location: {…}}` RECORDS, so the old
 *     generic `movePoint(v)` read `v.x` / `v.y` off the record itself, found
 *     neither, and returned the vertex untouched.
 * They were never alone: a `Point`'s `location`, a Hatch's boundary paths, a
 * Spline's control/fit points, a Wipeout's / Revision Cloud's vertices, a
 * Viewport's center, an Ellipse's frame and a Multileader's leader roots were
 * all equally invisible to the field list. `transformEntityCoords` knows every
 * one of them, per entity kind, with the OCS / refusal / direction-vector rules
 * (see {@link transformEntityCoords}'s arms) that a second copy here could only
 * get wrong — which is why this delegates rather than growing a third transform.
 *
 * WHAT MOVES AND WHAT DELIBERATELY DOES NOT, for the three round-15/16 kinds:
 *   - heavy `Polyline` — every VERTEX `location` moves (and, for a non-default
 *     group-210 extrusion, through the OCS map). Its `elevation` is the DXF
 *     "dummy point" whose X/Y are structurally 0 and whose Z is the plane
 *     height: a planar translation may NOT write X/Y into it (that would
 *     fabricate a group 10/20 the file never had), and Z is untouched because
 *     the rebase is planar. `transformEntityCoords` touches `elevation` only on
 *     the OCS path and only its `z`, which is the moved PLANE — correct, and
 *     the reason this must not be re-derived locally.
 *   - `Table` — `insertionPoint` moves (through `transformPoint3D`, so group 30
 *     survives). `horizontalDirection` (group 11/21/31) is a DIRECTION VECTOR,
 *     not a point: it rides the LINEAR part only, so a pure translation leaves
 *     it bit-identical. Translating it would re-aim every table.
 *   - `OLE Frame` — both stored corners move; the arm folds all four corners of
 *     the rectangle and writes back the source's own corner ROLES.
 */
function translateEntityCoords<T extends DrawingEntity>(
  entity: T,
  dx: number,
  dy: number,
): T {
  return transformEntityCoords(entity, (p) => translatePoint(p, -dx, -dy));
}

/**
 * The document-wide context {@link extractRefs} needs to resolve an arrowhead
 * NAME / imported `1005` HANDLE override to the block it actually points at
 * (the R12 canonical-`_DOT` adopt and the R13 handle → BLOCK_RECORD name join).
 * Built once per export and threaded through both dependency walks.
 */
interface BlockRefContext {
  definedBlockNames: ReadonlySet<string>;
  blockRecordHandlesToName: ReadonlyMap<string, string>;
}

function blockRefContext(source: DrawingDocument): BlockRefContext {
  return {
    definedBlockNames: new Set(
      Object.keys(source.blocks).map((n) => n.toLowerCase()),
    ),
    blockRecordHandlesToName: blockRecordHandlesToNameFromDocument(source),
  };
}

/**
 * Every BLOCK-table name `entity` depends on, as the CANONICAL key of
 * `source.blocks` (or nothing, when the document declares no such block).
 *
 * ONE truth, shared with PURGE: {@link extractRefs} is the same per-type
 * reference map `buildDependencyIndex` (and therefore `purgeBlocksMutation`
 * below) reads, so "what WBLOCK must ship" and "what PURGE must keep" can never
 * disagree. A hand-written `entity.type === "Block"` test here is precisely how
 * this went wrong: admitting `Table` as a top-level AND block-child entity left
 * both WBLOCK walks blind to the ANONYMOUS `*T<N>` graphics block an ACAD_TABLE
 * names in its group 2 / 343 — the block that holds the table's whole rendered
 * picture (one MTEXT per cell plus the LINE grid). WBLOCK of a selected table,
 * or of a block containing one, emitted the ACAD_TABLE record with a reference
 * to a block that was NOT in the file, so the exported table reopened as an
 * empty husk (@codex round 16, FINDING 1). Routing both walks through
 * `extractRefs` fixes that kind AND the kinds the allow-list was equally blind
 * to — a MULTILEADER's content block and a DIMENSION's arrowhead-override
 * blocks — and keeps the next reference-bearing entity type from going stale
 * the same way.
 *
 * SL-18 is preserved verbatim: `extractRefs`'s ARRAYRECT arm gates on the
 * exporter's OWN `arrayRectEmitsAsMInsert(entity, true)` predicate, so a
 * non-associative real grid ships its MINSERT source block while an associative
 * anchor, an unresolved-ext-dict array or a 1×1 array ship nothing — the
 * exporter never references their `sourceBlockName` either (@codex SL-18 P2).
 *
 * `resolveSourceBlockName` does the last step: block names are CASE-FOLDED in
 * AutoCAD, so a reference spelled `gridcell` must copy the block stored under
 * `GridCell`. It returns the canonical stored key (or `undefined` for a name
 * the document does not declare, which is a harmless no-dependency — the
 * exported DXF cannot ship a block that does not exist).
 */
function entityBlockDependencies(
  entity: DrawingEntity | BlockChildEntity,
  source: DrawingDocument,
  ctx: BlockRefContext,
): string[] {
  // targeted-cad-mutation-testing — THE CANONICAL-NAME INVARIANT, and the
  // equivalent mutants that follow from it.
  //
  // INVARIANT: every name this function returns is a CANONICAL KEY of
  // `source.blocks`, because `resolveSourceBlockName` returns either such a key
  // or `undefined`, and `undefined` is filtered out on the next line. Proven by
  // the two tests "ships nothing — and does not throw — for a reference the
  // document does not declare" (an undeclared name yields no block and no
  // throw) and "resolves a table's graphics-block reference case-insensitively"
  // (a differently-cased name yields the block under its CANONICAL key).
  //
  // EQUIVALENT MUTANTS (all verified against the run, not assumed):
  //   · ArrayDeclaration `[]` → `["Stryker was here"]` on the accumulator
  //     below, and the same mutant on `buildDocFromBlock`'s `seeds`. The
  //     injected name is not a key of `source.blocks`, so the closure visits it
  //     once and {@link copyBlockDefinitions} copies nothing for it — no block,
  //     no BLOCK_RECORD, no entity, no group anywhere in the output. It becomes
  //     observable only for a document that declares a block literally named
  //     "Stryker was here".
  //   · ConditionalExpression `canonical !== undefined` → `true`, which pushes
  //     `undefined` into the result. It is absorbed by the `current ===
  //     undefined` guard in {@link collectBlockClosure} — the paired guard
  //     below, which exists FOR this shape (`Array.prototype.pop` is typed
  //     `string | undefined`).
  const out: string[] = [];
  for (const name of extractRefs(
    entity,
    ctx.definedBlockNames,
    ctx.blockRecordHandlesToName,
  ).blocks) {
    const canonical = resolveSourceBlockName(source.blocks, name);
    if (canonical !== undefined) out.push(canonical);
  }
  return out;
}

/**
 * The transitive closure of block definitions an export must carry: the seeds
 * plus every block their children reference, recursively. Shared by both WBLOCK
 * builders so "follow a block dependency" is stated once — the two walks had
 * drifted into two copies of the same loop, and the round-16 finding had to be
 * reported against both.
 *
 * A name with no definition in `source.blocks` is still visited (and simply
 * copies nothing), matching the old walks: a dangling reference is not an
 * error here, it is a document that already lacked the block.
 *
 * targeted-cad-mutation-testing EQUIVALENT MUTANTS — two DEFENSIVE guards that
 * {@link entityBlockDependencies}'s canonical-name invariant makes unreachable
 * through this module's public API, so no test can distinguish them:
 *   · `current === undefined || …` → `false || …`. `stack.pop()` is typed
 *     `string | undefined` and the loop condition already guarantees a
 *     non-empty stack, so the disjunct can only fire for an `undefined` PUSHED
 *     into the stack — which the invariant forbids.
 *   · `if (!def) continue` → `if (false) continue`. Every visited name is a key
 *     of `source.blocks` by the same invariant, so `def` is always defined; the
 *     mutant's `def.entities` would throw only on a name the invariant excludes.
 * Both guards stay: they are what keeps the OTHER equivalent mutants (a bogus
 * seed, an `undefined` seed) harmless instead of a crash, and removing a guard
 * because only a mutant can reach it is how the next refactor gets a TypeError.
 */
function collectBlockClosure(
  source: DrawingDocument,
  seeds: Iterable<string>,
  ctx: BlockRefContext,
): Set<string> {
  const visited = new Set<string>();
  const stack = Array.from(seeds);
  while (stack.length > 0) {
    const current = stack.pop();
    if (current === undefined || visited.has(current)) continue;
    visited.add(current);
    const def = source.blocks[current];
    if (!def) continue;
    for (const child of def.entities) {
      stack.push(...entityBlockDependencies(child, source, ctx));
    }
  }
  return visited;
}

/**
 * Deep-copy the named block definitions and their BLOCK_RECORDs into the
 * plain-object shape the export document is parsed from.
 *
 * The two halves are taken INDEPENDENTLY, each only when the source has it:
 * `blocks` and `blockRecords` are separate maps and an R12 document has a
 * BLOCKS section with no BLOCK_RECORD table at all, so pairing them
 * unconditionally turns such a document into a hard export failure
 * (`JSON.parse(JSON.stringify(undefined))` throws). Pinned by the test
 * "exports a block that has NO BLOCK_RECORD (an R12 document) without failing".
 *
 * targeted-cad-mutation-testing EQUIVALENT MUTANT: `if (source.blocks[name])` →
 * `if (true)`. Every name reaching here comes from {@link collectBlockClosure},
 * whose members are canonical keys of `source.blocks` by
 * {@link entityBlockDependencies}'s invariant — so the test is always true
 * already. It is kept as the structural twin of the BLOCK_RECORD test beside
 * it, which is NOT equivalent (that one is killed by the R12 test above).
 */
function copyBlockDefinitions(
  source: DrawingDocument,
  names: Iterable<string>,
): {
  blocks: Record<string, unknown>;
  blockRecords: Record<string, unknown>;
} {
  const blocks: Record<string, unknown> = {};
  const blockRecords: Record<string, unknown> = {};
  for (const name of names) {
    if (source.blocks[name]) {
      blocks[name] = JSON.parse(JSON.stringify(source.blocks[name]));
    }
    if (source.blockRecords[name]) {
      blockRecords[name] = JSON.parse(
        JSON.stringify(source.blockRecords[name]),
      );
    }
  }
  return { blocks, blockRecords };
}

/**
 * The document-level SYMBOL TABLES an export must carry so every NAME
 * reference an exported entity holds still resolves in the new file.
 *
 * `styleTables` (layers / text styles / dimension styles / multileader styles)
 * was always carried; `linetypes` is NOT part of it — it is a SEPARATE
 * top-level `DrawingDocument` field (`model.ts`: `linetypes: z.record(…)`,
 * seeded with the standard LTYPEs and merged with the file's own) — and both
 * WBLOCK builders left it behind. The schema's seed default then re-created
 * ONLY the standard linetypes, so an entity whose group 6 named a linetype
 * LOADED FROM THE FILE (`source: "file"`) exported with a reference to an LTYPE
 * record that was not in the file:
 *
 *   source document          audit clean (0 errors), `linetypes` has MYDASH
 *   WBLOCK → re-import       1 × UNDEFINED_LINETYPE `linetype "MYDASH" not
 *                            found` — the entity still carries `6/MYDASH`,
 *                            the LTYPE table holds the 44 seeds alone
 *
 * A dangling group 6 is the same class of defect as the round-16 missing
 * `*T<N>` block: the reference survives, its target does not.
 *
 * WHOLE MAP, not a filtered closure — deliberately, and for the same reason
 * `styleTables` is carried whole. A linetype is referenced from THREE places,
 * only one of which any entity walk can see: an entity's own group 6
 * (`extractRefs(...).linetype`), a LAYER's `linetype` (and every layer ships),
 * and a raw unsupported entity's group 6 inside `rawEntityGroups`
 * (`checkRawEntityLinetypeRefs`). Filtering to the entity-visible subset would
 * re-open the hole for the other two, so the export over-ships unused LTYPE
 * records exactly the way it over-ships unused layers and text styles. (AutoCAD
 * WBLOCK purges what the written objects do not use; over-shipping is a
 * fidelity gap on the SIZE of the file, never a dangling pointer — that
 * trade-off is the established policy of this builder, not a new one.)
 *
 * `linetypeTableHeader` is deliberately NOT carried with it: those are the
 * SOURCE file's LTYPE table-head groups, including its own group-5 table
 * handle. The WBLOCK output is a from-scratch document whose handles are minted
 * by `ensureConformantSkeleton`, so importing a foreign table handle into it
 * risks aliasing a minted one. The writer synthesises a conformant header when
 * the field is absent, which is what every other from-scratch export already
 * relies on.
 */
function carriedSymbolTables(source: DrawingDocument): {
  styleTables: DrawingDocument["styleTables"];
  linetypes: DrawingDocument["linetypes"];
} {
  return { styleTables: source.styleTables, linetypes: source.linetypes };
}

/**
 * The group-342 style handle an entity carries, or `undefined`.
 *
 * Keyed on the FIELD, never on `type === "Table"`: `tableStyleHandle` IS the
 * group-342 declaration (`entityPolylineTableOle.ts` is its one home), so "an
 * entity that carries the pointer" and "an entity that needs its style carried"
 * are the same set by construction. The round-16 finding was a hand-maintained
 * TYPE list that went stale; a type test here would be the same trap one field
 * narrower.
 *
 * The `in` test is the TYPE SYSTEM's, not a behavioural branch: it narrows the
 * entity union to the members that declare the field so the read type-checks.
 * At runtime both arms agree for an entity without the field — reading an
 * undeclared property yields `undefined` either way.
 */
function tableStyleHandleOf(
  entity: DrawingEntity | BlockChildEntity,
): string | undefined {
  return "tableStyleHandle" in entity ? entity.tableStyleHandle : undefined;
}

/**
 * The OBJECTS records a WBLOCK must CARRY because an exported entity HARD-POINTS
 * at them (@codex round 17 P2).
 *
 * An `ACAD_TABLE` names its style by HANDLE in group 342, and the style itself
 * lives as a `TABLESTYLE` record in the OBJECTS stream, filed under the root
 * dictionary's `ACAD_TABLESTYLE` child (`tableStyleLookup.ts`; ezdxf registers
 * the class with `dict_name="ACAD_TABLESTYLE"` in
 * `ezdxf/entities/acad_table.py`). The builders below copy the table and its
 * `*T<N>` graphics block, so without this the 342 travelled and its target did
 * not — the same shape of defect as the round-16 missing `*T<N>` block and the
 * dangling group-6 linetype above: the reference survives, its target does not.
 *
 * COPY THE RECORD, DO NOT REMAP THE POINTER. AutoCAD's WBLOCK is a wblock
 * CLONE, and a wblock clone "clones hard owners and hard pointers and ignores
 * any soft references" (Autodesk, ObjectARX Developer's Guide — *Deep Clone
 * Basics*); group 342 is in the DXF hard-pointer range 340-349, so the
 * TABLESTYLE travels with the table. Remapping the 342 instead had no target to
 * remap TO in any case: the conformant skeleton's `ACAD_TABLESTYLE` dictionary
 * is EMPTY, so "point it at a valid style in the exported document" would have
 * meant inventing a style — a hard-coded `Standard` in place of reading the file
 * (CLAUDE.md ПРИОРИТЕТ #1).
 *
 * The CARRY itself is `drawing-model`'s: the record has to be merged into the
 * OBJECTS stream the conformant skeleton builds, with a re-minted handle, a
 * dictionary owner + child entry and its dangling pointers pruned, and the
 * referring 342 rewritten to match. That is object-graph semantics and belongs
 * to the CAD core (ПРИОРИТЕТ #2.1), so it lives in
 * `dxfBridge.carriedObjectRecords.ts` and is handed to the export through the
 * `carriedObjectRecords` option — NOT through `rawObjectGroups`, which is one of
 * the by-construction IMPORTED-SOURCE signals `isImportedSourceDocument` reads
 * (a from-scratch document that sets it skips the skeleton entirely: measured on
 * the `documentWithTableStyle` fixture, 15 967 bytes of DXF collapse to 1 700).
 * This module's only job is to SAY which records an export needs.
 *
 * Walked over the ALREADY-BUILT export document rather than inside either
 * builder, so the `block` and `objects` flavours cannot drift apart on it — the
 * round-16 finding had to be reported against two copies of one walk, and this
 * is the same class of dependency. A style already resolvable in the export
 * document is skipped (the `drawing` flavour deep-clones the whole OBJECTS
 * stream and needs no carry), and a handle whose `TABLESTYLE` the SOURCE cannot
 * resolve either yields nothing — a table that was already broken upstream stays
 * exactly as broken rather than gaining an invented style.
 */
function collectCarriedObjectRecords(
  source: DrawingDocument,
  exported: DrawingDocument,
): CarriedObjectRecord[] {
  const records: CarriedObjectRecord[] = [];
  // No de-duplication here: `mergeCarriedObjectRecords` is the ONE place that
  // decides a record is already carried — by handle AND by the dictionary name
  // it would be filed under — so a second copy of that rule here could only
  // drift from it.
  const visit = (entity: DrawingEntity | BlockChildEntity): void => {
    // No "has a handle?" pre-check: `carriedTableStyleRecord` takes
    // `string | undefined` and already answers "nothing to carry" for a missing
    // or unresolvable handle. A guard here would be a second spelling of that
    // one rule, and the two could only ever drift apart.
    const record = carriedTableStyleRecord(
      source.rawObjectGroups,
      tableStyleHandleOf(entity),
    );
    if (record !== undefined) records.push(record);
  };
  for (const space of exported.spaces) {
    for (const entity of space.entities) visit(entity);
  }
  for (const block of Object.values(exported.blocks)) {
    for (const child of block.entities) visit(child);
  }
  return records;
}

/**
 * Build a new DrawingDocument containing just the given entities (cloned
 * and translated so basePoint becomes the origin) as its *Model_Space.
 * Associated block definitions referenced by any INSERT inside the
 * selection are also copied in full so the exported DXF is self-contained.
 */
function buildDocFromObjects(
  source: DrawingDocument,
  entityIds: string[],
  basePoint: { x: number; y: number },
  insertionUnits: number,
): DrawingDocument {
  const selectedIdSet = new Set(entityIds);
  const modelEntities: unknown[] = [];
  const ctx = blockRefContext(source);
  // Collect every block name a selected entity references — an INSERT's
  // blockName, a table's `*T<N>` graphics block, a MINSERT-eligible array's
  // source block, a multileader's content block, a dimension's arrowhead
  // overrides — so the corresponding blocks + blockRecords ship with the new
  // document (see {@link entityBlockDependencies}).
  const referencedBlockNames = new Set<string>();

  for (const space of source.spaces) {
    for (const entity of space.entities) {
      if (!selectedIdSet.has(entity.id)) continue;
      // Deep clone so downstream translation does not mutate the source.
      const cloned = JSON.parse(JSON.stringify(entity)) as DrawingEntity;
      // Re-assign the entity's spaceId to the new model-space id below.
      // We leave a placeholder here and fix it after we know the id.
      const translated = translateEntityCoords(
        cloned,
        basePoint.x,
        basePoint.y,
      );
      modelEntities.push(translated);
      for (const name of entityBlockDependencies(entity, source, ctx)) {
        referencedBlockNames.add(name);
      }
    }
  }

  // Walk referenced blocks to include their transitive dependencies.
  const visited = collectBlockClosure(source, referencedBlockNames, ctx);

  const modelSpaceId = "space-model";
  const { blocks: newBlocks, blockRecords: newBlockRecords } =
    copyBlockDefinitions(source, visited);

  // Re-parent every entity to the new model-space id.
  const reparented = modelEntities.map((e) => {
    const r = e as Record<string, unknown>;
    return { ...r, spaceId: modelSpaceId };
  });

  return drawingDocumentSchema.parse({
    id: "wblock-export",
    title: "WBLOCK Export",
    spaces: [
      {
        id: modelSpaceId,
        kind: "model",
        name: "Model",
        entities: reparented,
      },
    ],
    activeSpaceId: modelSpaceId,
    ...carriedSymbolTables(source),
    blocks: newBlocks,
    blockRecords: newBlockRecords,
    layouts: {},
    insunits: insertionUnits,
  });
}

/**
 * Build a new DrawingDocument from a single named block. The block's
 * own entities become the new document's *Model_Space; nested block
 * references are included recursively so the resulting DXF is
 * self-contained.
 */
function buildDocFromBlock(
  source: DrawingDocument,
  blockName: string,
  insertionUnits: number,
): DrawingDocument {
  const blockDef = source.blocks[blockName];
  if (!blockDef) {
    throw new Error(`Block "${blockName}" not found`);
  }
  const modelSpaceId = "space-model";

  // Translate the block's entities so basePoint becomes the origin.
  const basePoint = blockDef.basePoint;
  const ctx = blockRefContext(source);
  const modelEntities = blockDef.entities
    // ATTDEFs are block-child-only; they would fail top-level schema parse.
    // AutoCAD's WBLOCK of a block drops ATTDEFs (they live only in BLOCKS).
    .filter(
      (e): e is Exclude<BlockChildEntity, { type: "Attdef" }> =>
        e.type !== "Attdef",
    )
    .map((e) => {
      const cloned = JSON.parse(JSON.stringify(e)) as Exclude<
        BlockChildEntity,
        { type: "Attdef" }
      >;
      const translated = translateEntityCoords(
        cloned,
        basePoint.x,
        basePoint.y,
      );
      return {
        ...(translated as unknown as Record<string, unknown>),
        spaceId: modelSpaceId,
      };
    });

  // Include transitive block references — seeded from EVERY reference the
  // block's own children carry (an ATTDEF's too: it is dropped from the
  // exported *Model_Space above, but a reference it carries was never a
  // graphics dependency of the block's picture; `extractRefs`'s Attdef arm
  // yields no block refs, so the seed stays exact either way).
  const seeds: string[] = [];
  for (const child of blockDef.entities) {
    seeds.push(...entityBlockDependencies(child, source, ctx));
  }
  const referenced = collectBlockClosure(source, seeds, ctx);
  const { blocks: newBlocks, blockRecords: newBlockRecords } =
    copyBlockDefinitions(source, referenced);

  return drawingDocumentSchema.parse({
    id: "wblock-export",
    title: `WBLOCK ${blockName}`,
    spaces: [
      {
        id: modelSpaceId,
        kind: "model",
        name: "Model",
        entities: modelEntities,
      },
    ],
    activeSpaceId: modelSpaceId,
    ...carriedSymbolTables(source),
    blocks: newBlocks,
    blockRecords: newBlockRecords,
    layouts: {},
    insunits: insertionUnits,
  });
}

/**
 * WBLOCK export. Builds a fresh DrawingDocument in the correct shape for
 * each source kind, sets INSUNITS, then serialises through the existing
 * DXF writer pipeline (dxfBridge.exportDrawingDocumentToDxfString).
 */
export function wblockExportMutation(
  input: WblockExportInput,
): WblockExportResult {
  const suggestedFilename = ensureDxfExtension(input.filename);

  let newDoc: DrawingDocument;
  let summary: string;

  switch (input.source.kind) {
    case "block": {
      newDoc = buildDocFromBlock(
        input.drawingDocument,
        input.source.blockName,
        input.insertionUnits,
      );
      summary = `WBLOCK block=${input.source.blockName} → ${suggestedFilename}`;
      break;
    }
    case "drawing": {
      // Deep clone + override INSUNITS; everything else (blocks, records,
      // layouts, entities, style tables) is preserved verbatim. Re-parse
      // through the schema so defaulted fields stay consistent.
      const cloned = JSON.parse(
        JSON.stringify(input.drawingDocument),
      ) as DrawingDocument;
      newDoc = drawingDocumentSchema.parse({
        ...cloned,
        insunits: input.insertionUnits,
      });
      summary = `WBLOCK entire drawing → ${suggestedFilename}`;
      break;
    }
    case "objects": {
      if (input.source.entityIds.length === 0) {
        throw new Error("WBLOCK objects: entityIds must not be empty");
      }
      newDoc = buildDocFromObjects(
        input.drawingDocument,
        input.source.entityIds,
        input.source.basePoint,
        input.insertionUnits,
      );
      summary = `WBLOCK ${input.source.entityIds.length} object(s) → ${suggestedFilename}`;
      break;
    }
    default: {
      // Unreachable — discriminated union exhausted above.
      const _exhaustive: never = input.source;
      void _exhaustive;
      throw new Error("WBLOCK: unsupported source kind");
    }
  }

  // WBLOCK writes `newDoc` to a real .dxf file on disk (App.tsx
  // handleWriteBlockConfirm) — a genuine save-to-DXF seam, so it must produce a
  // conformant DXF. The block / objects sources are from-scratch documents
  // (gain the conformant skeleton + hex handles); the 'drawing' deep-clone of an
  // imported source carries its `provenance` marker through the JSON round-trip,
  // so `ensureConformantSkeleton` passes it through byte-faithfully.
  // @codex round 17 P2 — the OBJECTS records an exported entity hard-points at
  // (an ACAD_TABLE's 342 TABLESTYLE) travel with it. See
  // {@link collectCarriedObjectRecords}; the merge is the conformant skeleton's.
  const dxfText = exportDrawingDocumentToDxfString(newDoc, {
    ensureConformant: true,
    carriedObjectRecords: collectCarriedObjectRecords(
      input.drawingDocument,
      newDoc,
    ),
  });
  return { dxfText, suggestedFilename, summary };
}

// ═══════════════════════════════════════════════════════════════
// PURGE — remove unreferenced block definitions + records
// ═══════════════════════════════════════════════════════════════

export interface PurgeBlocksInput {
  drawingDocument: DrawingDocument;
  /**
   * When true (default), reserved / anonymous blocks (*Model_Space,
   * *Paper_Space, *D\d+, *U\d+, *X\d+, *E\d+) are never candidates.
   */
  skipReserved?: boolean;
}

export interface PurgeCandidate {
  blockName: string;
  /** True when the block has no references and can be safely removed. */
  canPurge: boolean;
  /**
   * Machine-readable reason code when `canPurge` is false:
   *   - "reserved"    → anonymous/system block
   *   - "in-use"      → referenced by at least one INSERT
   *   - "ready"       → ready to purge
   */
  reason: "reserved" | "in-use" | "ready";
  /**
   * Top-space and block-level INSERT references (block names that contain
   * an INSERT of this block). Empty when the block has no references.
   */
  dependentBlocks: string[];
  /** Count of INSERT entities in spaces that reference this block. */
  topLevelReferenceCount: number;
}

export interface PurgeBlocksAnalysisResult {
  candidates: PurgeCandidate[];
  summary: string;
}

/**
 * Analyse which blocks are unreferenced and safe to purge. Does NOT
 * modify the document — `applyPurgeBlocks` performs the actual removal
 * once the user has confirmed the candidate list in the UI.
 */
export function purgeBlocksMutation(
  input: PurgeBlocksInput,
): PurgeBlocksAnalysisResult {
  const { drawingDocument } = input;
  const skipReserved = input.skipReserved !== false;

  // PR2 (plan §13) — block INSERT references resolved through the shared reverse
  // dependency index instead of a dual linear scan (spaces + block defs). Each
  // dependent `EntityRef.container` is a SPACE id (a top-level INSERT) OR a
  // BLOCK-DEFINITION name (a nested INSERT inside that block) — the SAME signal
  // the old scan derived from `space.entities` vs `parentDef.entities`. We use
  // the document's `blocks` keys to classify a container as a block-def edge.
  // The index walks BOTH spaces and block defs once, the same single traversal
  // the old code did, and case-folds the INSERT blockName so a mixed-case
  // reference still resolves (parity-preserving + a latent case-sensitivity bug
  // in the old `Map<string,…>` scan is gone).
  //
  // PR2 Codex P2 (#217) — PURGE only queries getDependents("block", …), so
  // build a BLOCK-ONLY, no-ownerBuckets throwaway index. This avoids
  // allocating a layer EntityRef + forward bucket for every one of the
  // (potentially hundreds of thousands of) ordinary entities on a large
  // drawing — refs the old dual scan never materialised either. extractRefs
  // still yields the same block refs, so the mleader/dimension block
  // references the INSERT-only scan missed remain counted (parity preserved).
  const depIndex = buildDependencyIndex(drawingDocument, {
    kinds: new Set(["block"]),
    trackOwners: false,
  });
  // Set of block-definition names (the containers that represent nested-INSERT
  // parent edges); everything else is a top-level space container.
  const blockDefNames = new Set(
    Object.keys(drawingDocument.blocks).map((n) => n.toLowerCase()),
  );

  const candidates: PurgeCandidate[] = [];
  for (const name of Object.keys(drawingDocument.blocks)) {
    const reserved = isAnonymousBlockName(name);
    if (reserved && skipReserved) {
      candidates.push({
        blockName: name,
        canPurge: false,
        reason: "reserved",
        dependentBlocks: [],
        topLevelReferenceCount: 0,
      });
      continue;
    }
    // Dependents of THIS block name (case-insensitive). Split into top-level
    // space INSERT count and the set of parent block-definition names.
    let topLevel = 0;
    const parentSet = new Set<string>();
    for (const ref of depIndex.getDependents("block", name)) {
      if (blockDefNames.has(ref.container.toLowerCase())) {
        parentSet.add(ref.container);
      } else {
        topLevel += 1;
      }
    }
    const parents = Array.from(parentSet);
    if (topLevel > 0 || parents.length > 0) {
      candidates.push({
        blockName: name,
        canPurge: false,
        reason: "in-use",
        dependentBlocks: parents,
        topLevelReferenceCount: topLevel,
      });
      continue;
    }
    candidates.push({
      blockName: name,
      canPurge: true,
      reason: "ready",
      dependentBlocks: [],
      topLevelReferenceCount: 0,
    });
  }

  // Sort by canPurge desc (purgeable first) then by name for stable UI.
  candidates.sort((a, b) => {
    if (a.canPurge !== b.canPurge) return a.canPurge ? -1 : 1;
    return a.blockName.localeCompare(b.blockName);
  });

  const readyCount = candidates.filter((c) => c.canPurge).length;
  const summary = `PURGE analysis: ${readyCount} block(s) ready to purge of ${candidates.length} total`;
  return { candidates, summary };
}

export interface ApplyPurgeBlocksResult {
  updatedDocument: DrawingDocument;
  purged: string[];
  skipped: string[];
  summary: string;
}

/**
 * Remove the given block names from `blocks` and `blockRecords`. Any name
 * that is still referenced (by an INSERT entity or as a nested INSERT
 * inside another block) is silently skipped — the caller should have
 * already filtered by `purgeBlocksMutation().candidates`, but we repeat
 * the check here as a defence-in-depth for command-line bypasses.
 */
export function applyPurgeBlocks(
  document: DrawingDocument,
  blockNames: string[],
): ApplyPurgeBlocksResult {
  const analysis = purgeBlocksMutation({ drawingDocument: document });
  const readyNames = new Set(
    analysis.candidates.filter((c) => c.canPurge).map((c) => c.blockName),
  );
  const purged: string[] = [];
  const skipped: string[] = [];
  for (const name of blockNames) {
    if (readyNames.has(name)) purged.push(name);
    else skipped.push(name);
  }

  const nextBlocks: Record<string, unknown> = {};
  for (const [name, def] of Object.entries(document.blocks)) {
    if (purged.includes(name)) continue;
    nextBlocks[name] = def;
  }
  const nextBlockRecords: Record<string, unknown> = {};
  for (const [name, rec] of Object.entries(document.blockRecords)) {
    if (purged.includes(name)) continue;
    nextBlockRecords[name] = rec;
  }

  const updatedDocument = assembleDrawingDocument({
    ...document,
    blocks: nextBlocks,
    blockRecords: nextBlockRecords,
  });

  const summary =
    purged.length === 0
      ? "PURGE: no blocks removed"
      : `PURGE: removed ${purged.length} block(s) — ${purged.join(", ")}${
          skipped.length > 0 ? `; skipped ${skipped.length} still in use` : ""
        }`;
  return { updatedDocument, purged, skipped, summary };
}

// ═══════════════════════════════════════════════════════════════
// RENAME — rename a block with full cascade across all INSERTs
// ═══════════════════════════════════════════════════════════════

export type RenameBlockValidationError =
  | "empty"
  | "too-long"
  | "reserved-new"
  | "reserved-old"
  | "forbidden-chars"
  | "duplicate"
  | "unknown-old-name";

export interface RenameBlockInput {
  oldName: string;
  newName: string;
  drawingDocument: DrawingDocument;
}

export interface RenameBlockResult {
  /** New INSERT cascades — count of INSERTs whose blockName was rewritten. */
  cascadeCount: number;
  updatedDocument: DrawingDocument;
  summary: string;
}

/**
 * Validate a rename operation without touching the document. Returns
 * null on success, otherwise a structured error code the UI can
 * localise.
 */
export function validateRenameBlock(
  oldName: string,
  newName: string,
  document: DrawingDocument,
): RenameBlockValidationError | null {
  if (!document.blocks[oldName]) return "unknown-old-name";
  // The old name being system-generated is disallowed — a `*`-prefixed block
  // is AutoCAD's own, and renaming one desynchronises whatever references it.
  // AutoCAD's RENAME dialog hides every such block from the list.
  //
  // GitHub Codex review PR #785 round 2, F3 — this gate asks a DIFFERENT
  // question from the PURGE gate above (`isAnonymousBlockName` at the
  // `skipReserved` check), and the two questions have different answers for
  // the ACAD_TABLE graphics block `*T<N>`:
  //   - PURGE asks "may this be DELETED when NOTHING references it?" — for an
  //     ORPHANED `*T<N>` (its table erased) the answer is YES, which is why
  //     round 1 deliberately kept `*T<N>` OUT of `isAnonymousBlockName`:
  //     reserving it would trade a deletion bug for a permanent leak, and
  //     neither AutoCAD's nor ezdxf's `purge` does that. That decision stands
  //     — the PURGE call site above must KEEP `isAnonymousBlockName`.
  //   - RENAME asks "may this be RENAMED while something STILL references
  //     it?" — for `*T<N>` the answer is NO. {@link renameBlockNameInEntity}
  //     below rewrites only a `Block`'s `blockName` and the MINSERT-shaped
  //     `sourceBlockName`; it has no `Table` arm, so a renamed `*T<N>` moved
  //     the definition out from under every `Table.blockName`, which kept the
  //     OLD name — the table then resolved nothing and rendered EMPTY
  //     (`scene3d/adapters/typed/tableFromEntity.ts` returns `null` for a
  //     `*T<N>` it cannot resolve).
  //
  // Fixed by the PREDICATE, not by growing the cascade: the cascade is a
  // hand-maintained allow-list of referencing entity types, i.e. exactly the
  // silent-drop shape that produced this defect — the next reference-bearing
  // entity type would go stale the same way. `isRenameHiddenBlockName` is one
  // closed rule at one gate: EVERY `*`-prefixed name, which is precisely the
  // set AutoCAD generates for itself and never offers to RENAME. It can never
  // hide a user-authored name because `*` is in the command engine's own
  // `FORBIDDEN_BLOCK_NAME_CHARS` (`blockMutations.ts`'s `validateBlockName`),
  // so no name a user could create is `*`-prefixed in the first place.
  if (isRenameHiddenBlockName(oldName)) return "reserved-old";
  // Use validateBlockName for the new name to reuse the full rule set
  // (length / forbidden chars / reserved-prefix). We pass the existing
  // blocks map so the duplicate check fires.
  const existingForCheck = { ...document.blocks };
  delete existingForCheck[oldName]; // rename → remove collision with self
  const err = validateBlockName(newName, existingForCheck);
  if (err === "empty") return "empty";
  if (err === "too-long") return "too-long";
  if (err === "reserved") return "reserved-new";
  if (err === "forbidden-chars") return "forbidden-chars";
  if (err === "duplicate") return "duplicate";
  return null;
}

function renameBlockNameInEntity(
  entity: unknown,
  oldName: string,
  newName: string,
): unknown {
  const e = entity as Record<string, unknown>;
  if (e.type === "Block" && e.blockName === oldName) {
    return { ...e, blockName: newName };
  }
  // SL-18 — a non-associative Rectangular Associative Array re-emits as a MINSERT
  // of its `sourceBlockName` (DXF group 2), so that field IS a real BLOCK-table
  // reference and must be rewritten on rename too — else it goes stale and the
  // next export sees the source block missing and falls back to the grid-losing
  // `0 ARRAYRECT` path (@codex SL-18 P2). GATED on the exporter's OWN
  // `arrayRectEmitsAsMInsert` predicate (same one dependencyGraph / referenceAudit
  // use), NOT a bare name match: for an ASSOCIATIVE array anchor, an array with an
  // UNRESOLVED extension dictionary, or a 1×1 array, the exporter keeps the plain
  // `0 ARRAYRECT` block reference and never emits `sourceBlockName` as a live
  // reference — rewriting it there would mutate preserved-source metadata for a
  // name the DXF writer does not emit, contradicting the gated ref model (@codex
  // SL-18 R3 P2). Match case-insensitively because AutoCAD folds block names (the
  // source group-2 may differ in case from the block key) and rewrite to the new
  // canonical name. `sourceBlockDefined = true`: the block being renamed (oldName)
  // is by definition present (validateRenameBlock guarantees it) and the field
  // matched it, so the shape gate alone decides eligibility here.
  if (
    e.type === "Rectangular Associative Array" &&
    typeof e.sourceBlockName === "string" &&
    e.sourceBlockName.toLowerCase() === oldName.toLowerCase() &&
    arrayRectEmitsAsMInsert(e as unknown as ArrayRectEntity, true)
  ) {
    return { ...e, sourceBlockName: newName };
  }
  return entity;
}

/**
 * Rename a block with full cascade. Walks every DrawingSpace.entities
 * plus every block definition's nested entities, rewriting INSERT
 * blockName references from oldName → newName. Updates blocks[],
 * blockRecords[], and any LAYOUT entry's ownerBlockRecordHandle binding
 * (AutoCAD keeps layout handles, which are independent of the block
 * name, so the layout object itself does not need a name update — but
 * we still sanity-check the map).
 */
export function renameBlockMutation(
  input: RenameBlockInput,
): RenameBlockResult {
  const error = validateRenameBlock(
    input.oldName,
    input.newName,
    input.drawingDocument,
  );
  if (error !== null) {
    throw new Error(`RENAME ${input.oldName} → ${input.newName}: ${error}`);
  }

  const { oldName, newName, drawingDocument } = input;

  // 1. Rewrite blocks[]: move oldName → newName. Also rewrite nested INSERT
  //    references inside every block's entities.
  const nextBlocks: Record<string, unknown> = {};
  let cascadeCount = 0;
  for (const [name, def] of Object.entries(drawingDocument.blocks)) {
    const nextName = name === oldName ? newName : name;
    // Update the BlockDefinition.name field itself when this is the renamed block.
    const renamedDef: Record<string, unknown> = { ...def };
    if (name === oldName) renamedDef.name = newName;
    // Rewrite every nested INSERT child that references oldName.
    const mutatedEntities = (def.entities as unknown[]).map((child) => {
      const rewritten = renameBlockNameInEntity(child, oldName, newName);
      if (rewritten !== child) cascadeCount += 1;
      return rewritten;
    });
    renamedDef.entities = mutatedEntities;
    nextBlocks[nextName] = renamedDef;
  }

  // 2. Rewrite blockRecords[]: same key swap + name field.
  const nextBlockRecords: Record<string, unknown> = {};
  for (const [name, rec] of Object.entries(drawingDocument.blockRecords)) {
    const nextName = name === oldName ? newName : name;
    const renamedRec: Record<string, unknown> = { ...rec };
    if (name === oldName) renamedRec.name = newName;
    nextBlockRecords[nextName] = renamedRec;
  }

  // 3. Rewrite top-level entities in every space.
  const nextSpaces = drawingDocument.spaces.map((space) => ({
    ...space,
    entities: space.entities.map((entity) => {
      const rewritten = renameBlockNameInEntity(entity, oldName, newName);
      if (rewritten !== entity) cascadeCount += 1;
      return rewritten;
    }),
  }));

  const updatedDocument = assembleDrawingDocument({
    ...drawingDocument,
    blocks: nextBlocks,
    blockRecords: nextBlockRecords,
    spaces: nextSpaces,
  });

  const summary = `RENAME ${oldName} → ${newName}: cascaded ${cascadeCount} INSERT reference(s)`;
  return { updatedDocument, cascadeCount, summary };
}
